import { useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Container,
  Nav,
  NavItem,
  NavLink
} from 'reactstrap'
import Link from 'next/link'
import { SUPPORT_APP_IDS } from '~/lib/constants/apps'
import useDrag from '~/hooks/useDrag'
import { checkScope } from '~/lib/helpers/checkScope'

function SubheaderNavItem ({ name, href, appLink = false, activeItemRef = null }) {
  let navLink = null

  if (appLink) {
    navLink = <NavLink target='_blank' rel='noopener noreferrer' draggable='false'>{name}</NavLink>
  } else if (activeItemRef !== null) {
    navLink = <NavLink active innerRef={activeItemRef} draggable='false'>{name}</NavLink>
  } else {
    navLink = <NavLink draggable='false'>{name}</NavLink>
  }

  return (
    <NavItem>
      <Link href={href} passHref>{navLink}</Link>
    </NavItem>
  )
}

SubheaderNavItem.propTypes = {
  name: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  appLink: PropTypes.bool,
  activeItemRef: PropTypes.object
}

function SubheaderPages ({ pathname, activeItemRef, profile }) {
  const origamiPermission = useMemo(() => checkScope(profile, 'support.origami.view'))
  const pageInfo = [
    { name: 'FAQ', href: '/support/faqs', key: 0 },
    { name: 'Contacts', href: '/support/contacts', key: 1 },
    { name: 'Office Locations', href: '/support/office-locations', key: 2 }
  ]

  if (origamiPermission) {
    pageInfo.push({ name: 'Report An Incident', href: '/support/report-incident', key: 3 })
  }

  const pages = pageInfo.map(page => {
    const isActiveItem = pathname.startsWith(`${page.href.toLowerCase()}`)

    return isActiveItem
      ? <SubheaderNavItem name={page.name} href={page.href} activeItemRef={activeItemRef} key={page.key} />
      : <SubheaderNavItem name={page.name} href={page.href} key={page.key} />
  })

  return pages
}

SubheaderPages.propTypes = {
  pathname: PropTypes.string.isRequired,
  activeItemRef: PropTypes.object.isRequired
}

function SubheaderApps ({ profile }) {
  const appMap = {}
  for (const app of profile.user.applications) {
    appMap[app.id] = app
  }

  const apps = SUPPORT_APP_IDS.map(id => {
    const app = appMap[id]

    if (app) {
      return <SubheaderNavItem name={app.name} href={app.embedUrl} appLink key={app.id} />
    } else {
      return null
    }
  })

  return apps
}

SubheaderApps.propTypes = {
  profile: PropTypes.object.isRequired
}

function Subheader ({ url: { pathname }, profile }) {
  const activeItemRef = useRef()
  const drag = useDrag()

  // Scroll to active item
  useEffect(() => {
    if (activeItemRef && activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ block: 'end', inline: 'start' })
    }
  }, [pathname, activeItemRef])

  if (pathname.slice(0, 8) === '/support') {
    return (
      <div id='subheader'>
        <Container fluid>
          <nav id='subheader' className='no-scrollbar navbar navbar-expand-md' {...drag}>
            <Nav className='text-links mr-auto'>
              <SubheaderPages profile={profile} pathname={pathname} activeItemRef={activeItemRef} />
              <SubheaderApps profile={profile} />
            </Nav>
          </nav>
        </Container>
      </div>
    )
  }

  return null
}

Subheader.propTypes = {
  url: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired
}

export default Subheader
